// SidebarIPBrowser — Wansight-style twin panels in the global sidebar.
//
//   ┌─ IP ADDRESSES ────────────────────────┐
//   │ IP / Mask: [🔍  Search...           ] │
//   │ ▾ /0 <active zone>                    │
//   │   └─ ☁ 10.0.0.0/8   gofiber           │
//   │   └─ ☁ 172.16.0.0/12                  │
//   ├─ IP GROUPS ───────────────────────────┤
//   │ IP Group:  [🔍  Search...          ]  │
//   │ ● default                             │
//   │   production                          │
//   └───────────────────────────────────────┘
//
// Clicking a prefix jumps to /ip?q=<addr|prefix>.
// Clicking a zone sets it as the active zone (persisted).
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Spin } from "antd";
import {
  CloudOutlined,
  FolderOpenOutlined,
  SearchOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { useIPZone } from "../contexts/IPZoneContext";
import { api } from "../api";

export default function SidebarIPBrowser({ onNav }: { onNav?: () => void }) {
  const nav = useNavigate();
  const {
    zones,
    zonesLoading,
    activeZoneID,
    setActiveZoneID,
    activeNodes,
    nodesLoading,
  } = useIPZone();

  // collapsed / expanded panels
  const [ipOpen, setIpOpen] = useState(true);
  const [grpOpen, setGrpOpen] = useState(true);
  const [rootExpanded, setRootExpanded] = useState(true);

  // local filters
  const [ipFilter, setIpFilter] = useState("");
  const [grpFilter, setGrpFilter] = useState("");

  // remote search (when ipFilter doesn't match any local node, fall back to
  // the /ip?q= endpoint so users can still jump to out-of-zone lookups)
  const [remoteResults, setRemoteResults] = useState<
    Array<{ id: number; prefix: string; description: string; match: string; addr?: string }>
  >([]);
  const [remoteBusy, setRemoteBusy] = useState(false);
  const [remoteErr, setRemoteErr] = useState<string | null>(null);
  const remoteToken = useRef(0);

  const trimmedIp = ipFilter.trim();

  const activeZone = useMemo(
    () => zones.find((z) => z.id === activeZoneID) ?? null,
    [zones, activeZoneID]
  );

  // Filter local zone nodes by the IP Mask search.
  const filteredNodes = useMemo(() => {
    if (!trimmedIp) return activeNodes;
    const q = trimmedIp.toLowerCase();
    return activeNodes.filter(
      (n) =>
        n.prefix.toLowerCase().includes(q) ||
        (n.description ?? "").toLowerCase().includes(q)
    );
  }, [activeNodes, trimmedIp]);

  // When filter is non-empty AND doesn't match any local node, kick off a
  // remote lookup via /api/v1/ip?q= so the user can still drill into any
  // prefix tracked by the database (debounced).
  useEffect(() => {
    if (!trimmedIp || filteredNodes.length > 0) {
      setRemoteResults([]);
      setRemoteErr(null);
      setRemoteBusy(false);
      return;
    }
    const myTok = ++remoteToken.current;
    setRemoteBusy(true);
    setRemoteErr(null);
    const t = setTimeout(async () => {
      try {
        const resp = await api.ipSearch(trimmedIp);
        if (myTok !== remoteToken.current) return;
        setRemoteResults(resp.nodes ?? []);
      } catch (e) {
        if (myTok !== remoteToken.current) return;
        setRemoteErr(e instanceof Error ? e.message : String(e));
        setRemoteResults([]);
      } finally {
        if (myTok === remoteToken.current) setRemoteBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [trimmedIp, filteredNodes.length]);

  const filteredZones = useMemo(() => {
    const q = grpFilter.trim().toLowerCase();
    if (!q) return zones;
    return zones.filter((z) => z.name.toLowerCase().includes(q));
  }, [zones, grpFilter]);

  function openPrefix(prefix: string) {
    nav(`/ip?q=${encodeURIComponent(prefix)}`);
    onNav?.();
  }

  return (
    <>
      {/* ── IP ADDRESSES ─────────────────────────────────────────── */}
      <div className="sb-ipb-panel">
        <button
          type="button"
          className="sb-ipb-header"
          onClick={() => setIpOpen((o) => !o)}
        >
          <span className="sb-ipb-title">IP Addresses</span>
          {activeZone && (
            <span className="sb-ipb-chip">
              {filteredNodes.length}
              {trimmedIp && filteredNodes.length !== activeNodes.length
                ? `/${activeNodes.length}`
                : ""}
            </span>
          )}
          <span className={`sb-ipb-caret ${ipOpen ? "open" : ""}`}>▾</span>
        </button>

        {ipOpen && (
          <>
            <div className="sb-ipb-search">
              <span className="sb-ipb-search-label">IP / Mask:</span>
              <Input
                size="small"
                allowClear
                placeholder="Search …"
                prefix={<SearchOutlined />}
                value={ipFilter}
                onChange={(e) => setIpFilter(e.target.value)}
                style={{ fontFamily: "monospace", fontSize: 11 }}
              />
            </div>

            <div className="sb-ipb-tree">
              {activeZoneID == null ? (
                <div className="sb-ipb-empty">Pick a zone below ↓</div>
              ) : nodesLoading ? (
                <div className="sb-ipb-empty">
                  <Spin size="small" /> Loading…
                </div>
              ) : (
                <>
                  {/* Root /0 */}
                  <div
                    className="sb-ipb-row sb-ipb-root"
                    onClick={() => setRootExpanded((e) => !e)}
                  >
                    <span
                      className={`sb-ipb-caret small ${rootExpanded ? "open" : ""}`}
                    >
                      ▾
                    </span>
                    <FolderOpenOutlined className="sb-ipb-icon root" />
                    <span className="sb-ipb-label">
                      /0
                      {activeZone && (
                        <span className="sb-ipb-meta"> {activeZone.name}</span>
                      )}
                    </span>
                  </div>

                  {rootExpanded && filteredNodes.length === 0 && !trimmedIp && (
                    <div className="sb-ipb-empty">No prefixes</div>
                  )}

                  {rootExpanded &&
                    filteredNodes.map((n) => {
                      const isV6 = n.prefix.includes(":");
                      return (
                        <div
                          key={n.id}
                          className="sb-ipb-row sb-ipb-leaf"
                          onClick={() => openPrefix(n.prefix)}
                          title={n.description || n.prefix}
                        >
                          <span className="sb-ipb-line">└</span>
                          <CloudOutlined
                            className={`sb-ipb-icon ${isV6 ? "v6" : "v4"}`}
                          />
                          <span className="sb-ipb-prefix">{n.prefix}</span>
                          {n.description && (
                            <span className="sb-ipb-desc">{n.description}</span>
                          )}
                        </div>
                      );
                    })}

                  {/* Remote-search fallback when local tree has no match */}
                  {trimmedIp && filteredNodes.length === 0 && (
                    <div className="sb-ipb-remote">
                      <div className="sb-ipb-remote-label">
                        No match in this zone
                        {remoteBusy && <Spin size="small" style={{ marginLeft: 6 }} />}
                      </div>
                      {remoteErr ? (
                        <div className="sb-ipb-empty err">{remoteErr}</div>
                      ) : remoteResults.length === 0 && !remoteBusy ? (
                        <div className="sb-ipb-empty">No database match either</div>
                      ) : (
                        remoteResults.slice(0, 8).map((r) => (
                          <div
                            key={`${r.id}-${r.addr ?? r.prefix}`}
                            className={`sb-ipb-row sb-ipb-leaf match-${r.match}`}
                            onClick={() => openPrefix(r.addr || r.prefix)}
                            title={r.description || r.prefix}
                          >
                            <CloudOutlined className="sb-ipb-icon v4" />
                            <span className="sb-ipb-prefix">
                              {r.addr || r.prefix}
                            </span>
                            <span className={`sb-ipb-match match-${r.match}`}>
                              {r.match}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── IP GROUPS ───────────────────────────────────────────── */}
      <div className="sb-ipb-panel">
        <button
          type="button"
          className="sb-ipb-header"
          onClick={() => setGrpOpen((o) => !o)}
        >
          <span className="sb-ipb-title">IP Groups</span>
          <span className="sb-ipb-chip">
            {filteredZones.length}
            {grpFilter && filteredZones.length !== zones.length
              ? `/${zones.length}`
              : ""}
          </span>
          <span className={`sb-ipb-caret ${grpOpen ? "open" : ""}`}>▾</span>
        </button>

        {grpOpen && (
          <>
            <div className="sb-ipb-search">
              <span className="sb-ipb-search-label">IP Group:</span>
              <Input
                size="small"
                allowClear
                placeholder="Search …"
                prefix={<SearchOutlined />}
                value={grpFilter}
                onChange={(e) => setGrpFilter(e.target.value)}
                style={{ fontSize: 11 }}
              />
            </div>

            <div className="sb-ipb-grouplist">
              {zonesLoading ? (
                <div className="sb-ipb-empty">
                  <Spin size="small" /> Loading…
                </div>
              ) : filteredZones.length === 0 ? (
                <div className="sb-ipb-empty">
                  {grpFilter ? `No match for "${grpFilter}"` : "No zones"}
                </div>
              ) : (
                filteredZones.map((z) => {
                  const active = z.id === activeZoneID;
                  return (
                    <div
                      key={z.id}
                      className={`sb-ipb-groupitem ${active ? "active" : ""}`}
                      onClick={() => {
                        // Set as active zone (drives the IP-Addresses tree above)
                        // AND navigate to the graph view for this whole zone.
                        setActiveZoneID(z.id);
                        nav(`/ip?zone=${z.id}`);
                        onNav?.();
                      }}
                      title={`Zone #${z.id} — click to graph`}
                    >
                      <TagOutlined className="sb-ipb-groupicon" />
                      <span className="sb-ipb-groupname">{z.name}</span>
                      {z.has_ipv6 && <span className="sb-ipb-v6">v6</span>}
                      {active && <span className="sb-ipb-dot" />}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
